import { DOCUMENT } from '@angular/common';
import { ExistingProvider, Injectable, inject } from '@angular/core';

import { Observable, filter, map } from 'rxjs';

import {
  DevBuild,
  EnvironmentService,
  LogoutService,
  TOKEN_STORE,
  TokenStore,
  hasOwnProp
} from '@hdms/shared/util-core';

import { ServiceWorkerService } from '../service-worker/service-worker.service';
import { UserContextService } from '../user';

import { TokenMessageInputData, TokenMessageOutputData, TokenMessageType } from './token-store.types';

type TokenServiceWorker = ServiceWorkerService<TokenMessageInputData, TokenMessageOutputData>;

/**
 * The service configures and communicates with its related TokenStoreWorker class, part of the service-worker.
 * It acts as a token store proxy, which passes the baton of shielding a token to the service-worker implementation.
 */
@Injectable({
  providedIn: 'root'
})
export class TokenStoreService implements TokenStore {
  private readonly env = inject(EnvironmentService);
  private readonly logout = inject(LogoutService);
  private readonly userContext = inject(UserContextService);
  private readonly serviceWorkerService = inject<TokenServiceWorker>(ServiceWorkerService);
  private readonly origin = inject(DOCUMENT).location.origin;

  private hasBearer = false;

  constructor() {
    this.setInitData();
    this.observeTokenChanges();
    this.observeAccountChanges();
  }

  get ready$(): Observable<void> {
    return this.serviceWorkerService.ready$;
  }

  get hasToken(): boolean {
    return this.hasBearer;
  }

  public setToken(token: string): void {
    token && this.postToken(token);
  }

  private setInitData(): void {
    const { isLoggedIn, ssoUserRole, customerOriginId } = this.userContext;
    this.serviceWorkerService.addInitData({
      tokenStore: {
        requestUrls: this.getRequestUrls(),
        enabled: isLoggedIn,
        userId: isLoggedIn ? [ssoUserRole, customerOriginId].join() : null
      }
    });
  }

  private postToken(token: string): void {
    this.serviceWorkerService.postMessage({ type: TokenMessageType.SET_TOKEN, tokenStore: { token } });
  }

  private observeAccountChanges(): void {
    if (this.userContext.isLoggedIn) {
      this.logout.switchAccount$().subscribe(() => this.postToken(''));
    }
  }

  /**
   * Subscribe to token presence messages from ServiceWorkerService.
   * @private
   */
  private observeTokenChanges(): void {
    this.serviceWorkerService.message$
      .pipe(
        map(({ tokenStore }) => tokenStore),
        filter(data => hasOwnProp(data, 'hasToken'))
      )
      .subscribe(({ hasToken }) => {
        this.hasBearer = hasToken;
      });
  }

  /**
   * Whitelist of urls to be intercepted and enriched with a token header.
   * As cloud urls are specific per tier, they have to be applied dynamically.
   * @private
   */
  private getRequestUrls(): string[] {
    return [
      this.env.dusBaseUrl,
      this.env.cloudBaseUrl,
      // required for local development
      this.env.devBuild === DevBuild.NGC && !this.env.gqlEndpointUri?.startsWith('http')
        ? this.origin + this.env.gqlEndpointUri
        : ''
    ].filter(Boolean);
  }
}

export const TOKEN_STORE_PROVIDER: ExistingProvider = {
  provide: TOKEN_STORE,
  useExisting: TokenStoreService
};
